"use client";

import { useState } from "react";
import { z } from "zod";
import Link from "next/link";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight, Send } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { FormSuccessState, MotionButton } from "@/components/motion";
import { buttonVariants } from "@/components/ui/button-variants";
import { footerNewsletter } from "@/content/footer";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { api } from "@/lib/api-client";
import { cn } from "@/lib/utils";

export function FooterNewsletter() {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const { submit, status, error, reset, isLoading } = useFormSubmit(
    (data: { email: string }) => api.subscribeNewsletter(data)
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);

    const result = z.string().email().safeParse(email.trim());
    if (!result.success) {
      setEmailError("Enter a valid email address");
      return;
    }

    const ok = await submit({ email: email.trim() });
    if (ok) setEmail("");
  };

  return (
    <div className="footer-newsletter rounded-xl px-4 py-3 md:px-5 md:py-3.5">
      <AnimatePresence mode="wait">
        {status === "success" ? (
          <FormSuccessState
            key="success"
            message={footerNewsletter.successMessage}
            className="border-accent/30 bg-card"
          >
            <Button variant="ghost" size="sm" onClick={reset}>
              Subscribe another email
            </Button>
          </FormSuccessState>
        ) : (
          <motion.div
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
              <div className="flex shrink-0 items-center gap-2.5 md:max-w-[18rem]">
                <div className="footer-newsletter-icon flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
                  <Send size={16} aria-hidden />
                </div>
                <div className="min-w-0">
                  <h3 className="footer-heading text-base font-semibold text-accent">
                    {footerNewsletter.heading}
                  </h3>
                  <p className="footer-body mt-0.5 text-sm text-muted">
                    {footerNewsletter.description}
                  </p>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                className="flex w-full max-w-xs flex-col gap-2 sm:flex-row sm:items-center md:ml-auto"
              >
                <Input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError(null);
                  }}
                  placeholder={footerNewsletter.placeholder}
                  required
                  autoComplete="email"
                  className={cn(
                    "footer-newsletter-input h-9 min-w-0 flex-1 text-sm",
                    emailError && "border-red-500"
                  )}
                  aria-label="Email address"
                  aria-invalid={!!emailError}
                  aria-describedby={
                    emailError ? "newsletter-email-error" : undefined
                  }
                />
                <MotionButton
                  type="submit"
                  isLoading={isLoading}
                  loadingText="Subscribing..."
                  disabled={isLoading}
                  className={buttonVariants({
                    variant: "accent",
                    size: "sm",
                    className: "footer-newsletter-submit h-9 shrink-0",
                  })}
                >
                  {footerNewsletter.submitLabel}
                  <ChevronRight size={14} aria-hidden />
                </MotionButton>
              </form>
            </div>

            {emailError && (
              <p
                id="newsletter-email-error"
                role="alert"
                className="mt-2 text-xs text-red-400"
              >
                {emailError}
              </p>
            )}

            <p
              role="status"
              aria-live="polite"
              className={cn(
                "mt-2 text-xs",
                status === "error" ? "text-red-400" : "sr-only"
              )}
            >
              {status === "error" &&
                (error ?? "Subscription failed. Please try again.")}
            </p>

            <p className="footer-body mt-2 text-[11px] text-muted">
              {footerNewsletter.privacyNote}{" "}
              <Link
                href="/privacy-policy"
                className="footer-link hover:text-accent"
              >
                Privacy Policy
              </Link>
              <span className="mx-1.5 text-border" aria-hidden>
                ·
              </span>
              <Link
                href="/newsletter/unsubscribe"
                className="footer-link text-muted/80 hover:text-accent"
              >
                Unsubscribe
              </Link>
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
